import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchActivities } from './api';
import { FocusActivitiesToggle, SortBar } from './components';
import { useDeviceInfo, useQuery } from './hooks';
import './index.css';
import { c } from './lib';
import type { SidebarActivity, SortType, SideBarAPIResult, SidebarActivityObject } from './types';

const ActivitiesSidebar: React.FC = () => {
  const { mw } = useDeviceInfo();

  const containerRef = useRef<HTMLDivElement>(null);

  const [sort, setSort] = useState<SortType>('alphabet');
  const [onlyFocus, setOnlyFocus] = useState(false);

  const api_data = useQuery<SideBarAPIResult>(fetchActivities, {"activities":[], "activities_by_skill_category":{}});
  const activities = api_data.activities;
  const activities_by_skill_category = api_data.activities_by_skill_category;

  const finalActivities = useMemo(() => {
    return activities
      .filter((activity) => !onlyFocus || activity.focus)
      .sort((a1, a2) => {
        switch (sort) {
          case 'alphabet':
            return a1.name.localeCompare(a2.name);
          case 'score':
            return (a2.score || 0) - (a1.score || 0);
          case 'recent':
            return a2.last_use - a1.last_use;
          default:
            return 0;
        }
      });
  }, [activities, onlyFocus, sort]);

  return (
    <div className="flex h-full flex-col" ref={containerRef}>
      <div className="bg-[#f7f7f7] pb-4 text-xs">
        <SortBar
          sorts={[
            {
              key: 'alphabet',
              label: (
                <>
                  A <span className="text-[#bfbfbf]">to</span> Z
                </>
              ),
            },
            {
              key: 'score',
              label: 'Score',
            },
            {
              key: 'recent',
              label: 'Latest',
            },
          ]}
          selectedSort={sort}
          setSelectedSort={setSort}
          overrideMobile={mw <= 1600}
        />
      </div>
      <ActivitiesList
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#eeeeee]"
        activities={finalActivities}
        activities_by_skill_category = {activities_by_skill_category}
        onlyFocus = {onlyFocus}
        sort = {sort}
      />
      <div className={c('bg-[#f7f7f7] pt-4')}>
        <FocusActivitiesToggle
          onlyFocus={onlyFocus}
          setOnlyFocus={setOnlyFocus}
          overrideMobile={mw <= 1650}
        />
      </div>
    </div>
  );
};

export default ActivitiesSidebar;

interface ActivitiesListProps extends React.HTMLAttributes<HTMLUListElement> {
  activities: SidebarActivity[];
  activities_by_skill_category:SidebarActivityObject;
  onlyFocus:boolean;
  sort:string
}

const ActivitiesList: React.FC<ActivitiesListProps> = ({
  activities,
  activities_by_skill_category,
  onlyFocus,
  sort,
  ...rest
}) => {

  const [finalList, setFinalList] = useState<SidebarActivity[]>();
  const [collapseFlag, setCollapseFlag] = useState<{[key: string]: boolean}>({});
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    const makeSortedFinalList = (activities:SidebarActivity[], activities_by_skill_category:SidebarActivityObject, onlyFocus:boolean, sort:string)  => {
      var result:SidebarActivity[] = [];
      if (activities_by_skill_category && Object.keys(activities_by_skill_category).length > 0){
        var added_activity_ids:string[] = [];
        var temp_flags = collapseFlag;
        activities.map(activity => {
          if (!added_activity_ids.includes(activity.id)){
            if (activity.is_grouping){
              if (activity.skill_category_id && activities_by_skill_category[activity.skill_category_id]){
                if (!temp_flags[activity.skill_category_id]){
                  temp_flags[activity.skill_category_id] = false;
                }
                var activities_in_group = activities_by_skill_category[activity.skill_category_id];
                activities_in_group = activities_in_group.filter((activity_in_group) => !onlyFocus || activity_in_group.focus)
                .sort((a1, a2) => {
                  switch (sort) {
                    case 'alphabet':
                      return a1.name.localeCompare(a2.name);
                    case 'score':
                      return (a2.score || 0) - (a1.score || 0);
                    case 'recent':
                      return a2.last_use - a1.last_use;
                    default:
                      return 0;
                  }
                });
                activities_in_group.map((activity_in_group, index) => {
                  activity_in_group.show_cateory = false;
                  if (index === 0){
                    activity_in_group.show_cateory = true;
                  }
                  result.push(activity_in_group);
                  added_activity_ids.push(activity_in_group.id);
                  return false;
                })
              }
            } else {
              result.push(activity);
              added_activity_ids.push(activity.id);
            }
            
          }
          return false;
        })
        setCollapseFlag(temp_flags);
      } else {
        return activities;
      }
      return result;
    }
    setFinalList(makeSortedFinalList(activities, activities_by_skill_category, onlyFocus, sort));
  }, [activities, activities_by_skill_category, onlyFocus, sort])
  
  const hideOrShow = (category_id : string|undefined, type:string) => {
    var temp_flags = collapseFlag;
    if (type === 'show'){
      if (category_id){
        temp_flags[category_id] = false;
      }
      
    } else {
      if (category_id){
        temp_flags[category_id] = true;
      }
    }
    setCollapseFlag(temp_flags);
    setRefresh(!refresh);
  }
  
  return (
    <ul {...rest}>
      {finalList && finalList.map((activity, i) => {
        const last = i === activities.length - 1;

        return (
          <>
          {activity.show_cateory && (
            <div className='py-4 px-3 flex justify-between'>
              <div className='font-inter !font-semibold text-[14px] text-[#555555] lg:text-[15px] 2lg:text-[17px]'><div className='lg:px-2'>{activity.skill_category_name}</div></div>
              {collapseFlag && activity.skill_category_id && collapseFlag[activity.skill_category_id] === true ? (
                <div onClick={() => hideOrShow(activity.skill_category_id, 'show')}
                  style={{borderRadius:'2px', cursor:'pointer', width: 0, height:0, borderTop:'5px solid transparent', borderBottom:"5px solid transparent", borderLeft:"10px solid #9b9b9b", marginTop:'10px'}}>
                </div>
              ) :(
                <div onClick={() => hideOrShow(activity.skill_category_id, 'hidden')}
                style={{borderRadius:'2px',cursor:'pointer',width: 0, height:0, borderLeft:'5px solid transparent', borderRight:"5px solid transparent", borderTop:"10px solid #9b9b9b", marginTop:'10px'}}></div>
              )}
            </div>
          )}
          <li key={activity.id} 
            className={c(activity.skill_category_id && parseInt(activity.skill_category_id)>0?"px-3 pl-5":'px-3',  
            (collapseFlag && activity.skill_category_id && collapseFlag[activity.skill_category_id] === true)?" hidden":"")}>
            <a
              className={c(
                'group flex h-full w-full items-center',
                (activity.skill_category_id && parseInt(activity.skill_category_id)>0)?'pl-4 border-l border-l-[#d8d8d8]':'',
              )}
              href={activity.page_hyperlink}
            >
              <div className={c(!last && 'border-b border-b-theme-border/40', 'py-3 flex h-full w-full group',
                (activity.skill_category_id && parseInt(activity.skill_category_id)>0)?'lg:px-2':'lg:px-2',
              )}>
                <span className={c("font-inter group-hover:text-[#009cc2]", 
                  (activity.skill_category_id && parseInt(activity.skill_category_id)>0)?"!font-medium text-[12px] text-[#777] lg:text-[13px] 2lg:text-[15px]"
                  :"!font-semibold text-[14px] text-[#555555] lg:text-[15px] 2lg:text-[17px]")}>
                  {activity.short_name || activity.name}
                </span>
                {activity.legacy ? (
                  <span className="ml-4 text-sm tracking-tight text-[#bbbbbb]">
                    LEGACY
                  </span>
                ) : null}
                {activity.score ? (
                  <span
                    className={c(
                      'ml-auto',
                      activity.median === 'below' && 'text-[#fc5656]',
                      activity.median === 'average' && 'text-[#415e81]',
                      activity.median === 'above' && 'text-[#7ce027]'
                    )}
                  >
                    <span className="font-inter text-[14px] !font-semibold lg:text-[15px] 2lg:text-[17px]">
                      {activity.score}
                    </span>
                    <span className="ml-1 text-[0.6rem] text-[#d1d1d1]">%</span>
                  </span>
                ) : null}
              </div>
            </a>
          </li>
          </>
        );
      })}
    </ul>
  );
};
